<template>
	<div class="quiz-overlay" @click.self="close">
		<div class="quiz-modal">
			<!-- Header -->
			<div class="quiz-modal__header">
				<div class="quiz-modal__header-container">
					<button
						v-if="currentStepIndex > 0 && !isResults"
						type="button"
						class="quiz-modal__back button-sq"
						aria-label="Назад"
						@click="goBack"
					>
						<i class="icon icon-arrow-left"></i>
					</button>
					<span v-else class="quiz-modal__back-placeholder"></span>
					<span class="quiz-modal__step-text">
						{{ isResults ? 'Результат' : `Шаг ${currentStepIndex + 1} из ${props.items.length}` }}
					</span>
					<button
						type="button"
						class="quiz-modal__close button-sq"
						aria-label="Закрыть"
						@click="close"
					>
						<i class="icon icon-close"></i>
					</button>
				</div>
			</div>

			<!-- Progress -->
			<div 
				class="quiz-modal__progress"
				:class="{
					'quiz-modal__progress-hidden':isResults
				}">
				<span
					v-for="(step, key) in props.items"
					:key="step.id"
					class="quiz-modal__progress-segment"
					:class="{ 'quiz-modal__progress-segment--filled': key <= currentStepIndex }"
				></span>
			</div>

			<div class="quiz-modal__container">
				<div class="quiz-modal__scroll">
					<!-- Body -->
					<div class="quiz-modal__body">
						<StepView
							v-if="!isResults && currentStep"
							:step="currentStep"
							:selected-ids="getSelections(currentStep.id)"
							@toggle="handleToggle"
						/>
						<ResultsView
							v-if="isResults"
							:id="props.id"
							:placement="props.placement"
							:results="results"
						/>
					</div>

					<!-- Footer -->
					<div class="quiz-modal__footer">
						<button
							v-if="!isResults"
							type="button"
							class="quiz-modal__action button"
							:class="{'button--loading': isLoading}"
							:disabled="!canProceed || isLoading"
							@click="goNext"
						>
							<span>{{ footerText }}</span>
							<i class="icon icon-arrow-right"></i>
						</button>
						<button
							v-else
							type="button"
							class="quiz-modal__action quiz-modal__action--restart button"
							@click="restart"
						>
							<i class="icon icon-arrow-left"></i>
							<span>Начать заново</span>
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import StepView from './quiz-step.vue';
import ResultsView from './quiz-results.vue';
import { fetchQuizResults } from '@/api/services/quiz';


interface Props {
	id: string;
	placement: string;
	items: QuizStep[]
	url: string;
	onClose: () => void;
}

const props = defineProps<Props>();

onMounted(() => {
	document.body.classList.add('modal-open');
	(window.dataLayer = window.dataLayer || []).push({
		event: 'quiz_event',
		quiz_event_name: 'quiz_open',
		quiz_category: props.id,
		quiz_placement: props.placement
	});
});

onBeforeUnmount(() => {
	document.body.classList.remove('modal-open');
});

const results = ref<QuizResult[]>([]);
const currentStepIndex = ref(0);
const selections = ref<Map<string, Set<string>>>(new Map());
const isResults = ref(false);
const isLoading = ref(false);

const currentStep = computed(() => props.items[currentStepIndex.value] || null);

const isLastStep = computed(() => currentStepIndex.value === props.items.length - 1);

const canProceed = computed(() => {
	if (!currentStep.value) return false;
	const selected = selections.value.get(currentStep.value.id);
	return selected ? selected.size > 0 : false;
});

const footerText = computed(() => {
	if (isLastStep.value && canProceed.value) return 'Показать результат';
	return 'Далее';
});

function getSelections(stepId: string): Set<string> {
	return selections.value.get(stepId) || new Set();
}

function handleToggle(itemId: string) {
	if (!currentStep.value) return;

	const stepId = currentStep.value.id;
	const current = new Set(selections.value.get(stepId) || []);

	if (currentStep.value.multiple) {
		if (current.has(itemId)) {
			current.delete(itemId);
		} else {
			current.add(itemId);
			(window.dataLayer = window.dataLayer || []).push({
				event: 'quiz_event',
				quiz_event_name: 'quiz_answer',
				quiz_category: props.id,
				quiz_placement: props.placement,
				quiz_question: currentStep.value.id,
				quiz_answer: itemId,
				quiz_step: currentStepIndex.value+1,
			});
		}
	} else {
		current.clear();
		current.add(itemId);
		(window.dataLayer = window.dataLayer || []).push({
			event: 'quiz_event',
			quiz_event_name: 'quiz_answer',
			quiz_category: props.id,
			quiz_placement: props.placement,
			quiz_question: currentStep.value.id,
			quiz_answer: itemId,
			quiz_step: currentStepIndex.value+1,
		});
	}

	selections.value.set(stepId, current);
}

function goNext() {
	if (!canProceed.value || isLoading.value) return;

	if (isLastStep.value) {
		submitResults();
	} else {
		(window.dataLayer = window.dataLayer || []).push({
			event: 'quiz_event',
			quiz_event_name: 'quiz_step_'+(currentStepIndex.value+1),
			quiz_category: props.id,
			quiz_placement: props.placement
		});
		currentStepIndex.value++;
		
	}
}

function goBack() {
	if (currentStepIndex.value > 0) {
		currentStepIndex.value--;
	}
}

async function submitResults() {
	if (!props.url) return;

	isLoading.value = true;

	const answers: QuizAnswers = {};
	selections.value.forEach((selectedSet, stepId) => {
		answers[stepId] = Array.from(selectedSet);
	});

	results.value = await fetchQuizResults(props.url, answers);

	isResults.value = true;
	isLoading.value = false;

	(window.dataLayer = window.dataLayer || []).push({
		event: 'quiz_event',
		quiz_event_name: 'quiz_result',
		quiz_category: props.id,
		quiz_placement: props.placement
	});
}

function restart() {
	currentStepIndex.value = 0;
	selections.value.clear();
	results.value = [];
	isResults.value = false;
}

function close() {
	if (isLoading.value) return;
	(window.dataLayer = window.dataLayer || []).push({
		event: 'quiz_event',
		quiz_event_name: 'quiz_close',
		quiz_category: props.id,
		quiz_placement: props.placement,
		quiz_step: currentStepIndex.value+1,
	});
	restart();
	props.onClose();
}
</script>